// @flow
import { List, Map as ImmutableMap } from 'immutable';

import { transformAccountToRequestData } from 'data/accounts/transformers';
import type { Account } from 'data/accounts/types';
import type { InstitutionLogin } from 'data/institutionLogins/types';
import { getLogger } from 'utils/logger';
import { replaceRawASCII } from 'utils/utils';

import * as types from './types';

const log = getLogger('containers/UpsertFinancialInstitution/sagas.js');

const transformAccountsToRequestData = (accounts: List<Account>) =>
  accounts.reduce((list, account) => { // eslint-disable-line
    list.push(transformAccountToRequestData(account));
    return list;
  }, []);

export const transformInstitutionLoginToReqestData = (institutionLogin: InstitutionLogin, accounts = null) => ({
  cpSetupMode: institutionLogin.cpSetupMode ? institutionLogin.cpSetupMode : 'DISCOVER_ACCOUNTS_ONLY',

  id: institutionLogin.id !== '0' ? institutionLogin.id : null,
  name: institutionLogin.name,

  cpInstitutionId: institutionLogin.cpInstitutionId,
  institutionId: institutionLogin.institutionId,

  credentials: !institutionLogin.credentials ? null : institutionLogin.credentials.map((credential) => ({
    key: credential.key,
    value: credential.value,
  })),
  aggregators: !institutionLogin.aggregators || institutionLogin.aggregators.size === 0 ? null :
    [{
      channel: institutionLogin.aggregators.get(0).channel,
    }],

  accounts: accounts != null ? transformAccountsToRequestData(accounts) : null,
});

export const transformResponseToAccountDiscoveryData = (data: any, allAccountsById: ImmutableMap<string, Account>) => {
  const activeAccounts = [];
  const deadAccounts = [];
  const newAccounts = [];
  const unconnectedAccounts = [];

  log.log('Accounts in response=', data.accounts);

  data.accounts.forEach((account) => {

    let existingAccount = null;
    if (account.id) {
      existingAccount = allAccountsById.get(account.id);
      // todo: should we fail if we cannot find existing account
    }

    // todo: need to handle multiple aggregators of different types, for now
    // todo: will will assume just a single aggregator and fix later
    //
    const aggregator = account.aggregators && account.aggregators.length > 0 ? account.aggregators[0] : null;
    log.log('Account and Aggregator', account, aggregator, aggregator && aggregator.cpDetails);

    // case a: no id - new discovered account (or one QCS does not know about)
    // case b: id but no aggregator - unconnected account
    // case c: id, cpId, cpDetails - active aggregated account
    // case d: id, cpId, but no cpDetails - dead aggregated account

    let discoveredAccount = null;
    if (aggregator && aggregator.cpDetails) {
      discoveredAccount = types.mkDiscoveredAccount({
        id: aggregator.cpId,
        channel: aggregator.channel,

        type: aggregator.cpDetails.type,
        subType: aggregator.cpDetails.subType,

        name: replaceRawASCII(aggregator.cpDetails.name),
        number: aggregator.cpDetails.maskedNumber,
        maskedNumber: aggregator.cpDetails.maskedNumber,

        balance: aggregator.cpDetails.balance,
        balanceAt: aggregator.cpDetails.balanceAt,

        currency: aggregator.cpDetails.currency,
      });
    }
    log.log('Discovered Account is', discoveredAccount);

    if (existingAccount) {
      if (discoveredAccount) {
        activeAccounts.push(
          types.mkActiveDiscoveredAccount({
            account: existingAccount,
            discoveredAccount,
          })
        );
      } else if (aggregator) {
        deadAccounts.push(existingAccount);
      } else {
        unconnectedAccounts.push(existingAccount);
      }
    } else {
      if (!discoveredAccount) {
        log.error("We didn't discover an existing account yet were also missing new account information", account);
        return;
      }
      const {aggStatus, type} = aggregator.cpDetails;

      if (aggStatus === 'ACCOUNT_NOT_FOUND') {
        log.info("Skipping account because FDS has marked it as not found and we don't know about it anyways", account);
        return;
      }

      if (type !== 'BANK' && type !== 'CREDIT' && type !== 'INVESTMENT' && type !== 'LOAN' && type !== 'UNKNOWN') {
        log.log(`Skipping unsupported account type, type=${type}`);
        return;
      }

      newAccounts.push(discoveredAccount);
    }
  });

  const accountDiscoveryData = types.mkAccountDiscoveryData({
    institutionLoginId: data.institutionLoginId,
    credentialsBlob: data.credentialsBlob,

    status: data.status,

    activeAccounts: List(activeAccounts),
    deadAccounts: List(deadAccounts),
    newAccounts: List(newAccounts),
    unconnectedAccounts: List(unconnectedAccounts),
  });

  log.log('AccountDiscoveryData = ', accountDiscoveryData);
  return accountDiscoveryData;
};

export function transformResponseToPartnerAuthUris(response: any) {
  const { data } = response;
  return List(data.uris);
}

export function transformResponseToPlaidPublicToken(response: any) {
  const { data } = response;
  return data.publicToken;
}

// ================================================================================================
// Transformers for AccountDiscovery Status
// ================================================================================================

export function transformResponseToAccountDiscoveryStatus(response: any) {
  const { data } = response;

  return types.mkAccountDiscoveryStatus({
    institutionLoginId: data.institutionLoginId,

    isProcessing: data.isProcessing,
    status: data.status,

    aggregators: !data.aggregators ? List() : List(
      data.aggregators.map((aggregator) => types.mkAccountDiscoveryAggregatorStatus({
        isProcessing: aggregator.isProcessing,

        aggStatus: aggregator.aggStatus,
        cpAggStatusCode: aggregator.cpAggStatusCode,
        cpAggStatusDetail: aggregator.cpAggStatusDetail,
      }))
    ),
  });
}

export function transformAccountsToDiscoverAccount(institutionLogin, accounts) {

  // const activeAccounts = [];
  // const deadAccounts = [];
  const newAccounts = [];
  // const unconnectedAccounts = [];

  accounts.forEach((account) => {

    const { type } = account;
    if (type !== 'BANK' && type !== 'CREDIT' && type !== 'INVESTMENT' && type !== 'LOAN') {
      log.info(`Skipping unsupported account type, type=${type}`);
      return;
    }

    const aggregator = account.aggregators.get(0);
    const discoveredAccount = types.mkDiscoveredAccount({
      id: aggregator.cpId,
      channel: aggregator.channel,

      type,
      subType: account.subType,

      name: account.name,
      maskedNumber: aggregator.accountNumberMasked,
    });

    newAccounts.push(discoveredAccount);
  });

  const accountDiscoveryData = types.mkAccountDiscoveryData({
    institutionLoginId: institutionLogin.id,
    status: 'OK',

    // activeAccounts: List(activeAccounts),
    // deadAccounts: List(deadAccounts),
    newAccounts: List(newAccounts),
    // unconnectedAccounts: List(unconnectedAccounts),
  });

  return accountDiscoveryData;
}

export const transformPlaidItemActivityToReqestData = (institutionLogin: InstitutionLogin, event) => ({
  event: event.name,
  sessionId: event.sessionId,
  requestId: event.requestId,

  institutionLoginId: institutionLogin ? institutionLogin.id : null,

  institutionId: event.institutionId,
  institutionName: event.institutionName,

  errorCode: event.errorCode,
  errorMessage: event.errorMessage,
  exitedAt: event.exitedAt,
});


